// Copyright (c) 2026 CTO's Club LLC. All rights reserved.

import { z } from 'zod';
import { IpxRbsWorkflowWebsiteScopeSchema } from './rbs-website-scope.schemas';

export const WorkflowExecutionObservationMessage = 'Workflow execution completed';

export const WorkflowExecutionObservationV1Schema = z
    .object({
        schemaVersion: z.literal(1),
        observationId: z.string().uuid(),
        workflowId: z.string().min(1),
        startOperationId: z.string().min(1).nullable(),
        startedAtMs: z.number().int().nonnegative(),
        completedAtMs: z.number().int().nonnegative(),
        success: z.boolean(),
        completedOperations: z.number().int().nonnegative(),
        totalOperations: z.number().int().nonnegative(),
        lastOperationId: z.string().min(1).nullable(),
        executionTime: z.number().int().nonnegative(),
    })
    .strict();

export type WorkflowExecutionObservation = z.infer<typeof WorkflowExecutionObservationV1Schema>;

export const WorkflowExecutionObservationContract = {
    variants: { v1: { normalize: WorkflowExecutionObservationV1Schema.parse } },
    writerVariant: 'v1',
} as const;

export const WorkflowExecutionObservationLogV1Schema = z
    .object({
        message: z.literal(WorkflowExecutionObservationMessage),
        args: z.tuple([WorkflowExecutionObservationV1Schema]),
    })
    .passthrough();

export const DebugRpaWorkflowExecutionIdentitySchema = z
    .object({
        executionId: z.string().uuid(),
        workflowId: z.string().min(1),
        workflowName: z.string(),
        tabId: z.number().int().positive().nullable(),
        webContentsId: z.number().int().positive(),
        startedAtMs: z.number().int().nonnegative(),
        triggerKey: z.union([z.string().min(1), z.number()]).nullable(),
        rbsProjection: z
            .object({
                projectionId: z.string().min(1),
                ipxWebResourceModelRevision: z.string().min(1),
            })
            .strict()
            .nullable(),
    })
    .strict();

export const DebugRpaRecentWorkflowExecutionSnapshotSchema =
    DebugRpaWorkflowExecutionIdentitySchema.extend({
        success: z.boolean(),
        completedOperations: z.number().int().nonnegative(),
        totalOperations: z.number().int().nonnegative(),
        lastOperationId: z.string().min(1).nullable(),
        executionTime: z.number().int().nonnegative(),
        completedAtMs: z.number().int().nonnegative(),
        data: z.unknown().optional(),
        errorMessage: z.string().nullable(),
    }).strict();

export const DebugRbsIpsRegisteredWorkflowSnapshotSchema = z
    .object({
        workflowId: z.string().min(1),
        state: z.literal('registered'),
        projectionId: z.string().min(1),
        websiteScope: IpxRbsWorkflowWebsiteScopeSchema,
        allowedWebsiteOwner: IpxRbsWorkflowWebsiteScopeSchema,
        workflowClass: z.string().min(1),
        instanceCount: z.number().int().nonnegative(),
        ipxWebResourceModelUuid: z.string().uuid(),
        ipxWebResourceModelRevision: z.string().min(1),
    })
    .strict();

export type SimpleRpaActiveWorkflowExecutionInfo = Readonly<
    Omit<z.infer<typeof DebugRpaWorkflowExecutionIdentitySchema>, 'rbsProjection'>
> & {
    readonly rbsProjection: Readonly<
        NonNullable<z.infer<typeof DebugRpaWorkflowExecutionIdentitySchema>['rbsProjection']>
    > | null;
};

export type SimpleRpaRecentWorkflowExecutionInfo = SimpleRpaActiveWorkflowExecutionInfo &
    Readonly<
        Omit<
            z.infer<typeof DebugRpaRecentWorkflowExecutionSnapshotSchema>,
            keyof SimpleRpaActiveWorkflowExecutionInfo
        >
    >;

export type DebugRbsIpsRegisteredWorkflowSnapshot = z.infer<
    typeof DebugRbsIpsRegisteredWorkflowSnapshotSchema
>;
